// SegmentedControl — Phase 4 Plan 04-06 Task 2 (UI-SPEC §Screen 7).
//
// 2+ button pill group with accent fill on the selected option. Used by
// the Reports screen for the xlsx | pdf format selector.
//
// Accessibility:
//   - role="radiogroup" with `aria-label`
//   - each button: role="radio" + aria-checked
//   - keyboard: tab through buttons; Enter/Space toggles
//
// Visual notes: matches Telegram-style segmented controls. Uses
// `--tg-theme-button-color` as accent fallback.

import type { CSSProperties } from "react";

export interface SegmentedControlOption {
  value: string;
  label: string;
}

export interface SegmentedControlProps {
  value: string;
  onChange: (next: string) => void;
  options: SegmentedControlOption[];
  ariaLabel: string;
  disabled?: boolean;
}

const ACCENT = "var(--tg-theme-button-color, #1E63CE)";
const ACCENT_TEXT = "var(--tg-theme-button-text-color, #ffffff)";
const NEUTRAL_BG = "var(--tg-theme-bg-color, #f1f3f5)";
const NEUTRAL_TEXT = "var(--tg-theme-text-color, #1a1a1a)";

const GROUP_STYLE: CSSProperties = {
  display: "inline-flex",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid #d1d5db",
  background: NEUTRAL_BG,
};

const BTN_BASE: CSSProperties = {
  border: "none",
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 80,
  minHeight: 36,
};

export function SegmentedControl({
  value,
  onChange,
  options,
  ariaLabel,
  disabled = false,
}: SegmentedControlProps): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid="segmented-control"
      style={GROUP_STYLE}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const btnStyle: CSSProperties = {
          ...BTN_BASE,
          background: selected ? ACCENT : "transparent",
          color: selected ? ACCENT_TEXT : NEUTRAL_TEXT,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        };
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`segmented-${opt.value}`}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (opt.value !== value) onChange(opt.value);
            }}
            style={btnStyle}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
